import React, { useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import { generateUUID, getLayout, i18nIntl, isNotEmpty, ensureBoolean } from '../../lib/ep.js';
import { Route, HashRouter as Router, Switch } from 'react-router-dom';

import ActionButton from '../builder/ActionButton';
import BuilderCanvas from '../builder/BuilderCanvas';
import BuilderMain from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar';
import ButtonCollection from '../builder/ButtonCollection.js';
import ColorPicker from '../builder/ColorPicker.js';
import DynamicFields from '../builder/DynamicFields.js';
import { AutoPostImageUploader } from '../builder/ImageUploader.js';
import Input from '../builder/Input';
import { NavigationTabsBuilder } from '../builder/NavigationTabs';
import Model from '../builder/Model';
import Range from '../builder/Range.js';
import RichEditor from '../builder/RichEditor';
import Select from '../builder/Select.js';
import Spinner from '../builder/Spinner';

import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';

import { GameContainerWrapper } from '../front/GameContainer.js';
import Layout from '../front/Layout';
import StackItClient from '../front/StackItClient.js';

import transferPlaceholder from '../../hoc/transferPlaceholder';
import Message from '../builder/Message.js';
import Checkbox from '../builder/Checkbox.js';
import DisplayList from '../builder/DisplayList.js';
import SoundSelect from '../builder/SoundSelect.js';
import GameInstructionsBuilder from './GameInstructionsBuilder.js';
import GameScoreBuilder from './GameScoreBuilder.js';
import GameSoundtrackFields from '../builder/GameSoundtrackFields.js';

const InputPlaceHolder = injectIntl(transferPlaceholder(Input));

const StackItField = ({ model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ uploading, setUploading ] = useState(false);

	const auto_img_hooks = (hook) => {
		switch(hook.type) {
			case 'AUTO_IMAGE_START':
				setUploading(true);
				break;

			case 'AUTO_IMAGE_END':
				setUploading(false);
				break;
		}
	}

	const can_delete = ensureBoolean(model?.can_delete ?? true);

	return (
		<Model
			model={model}
			change={change}
			errors={props.errors != undefined && props.errors[model.id]}
		>
			{(can_delete) && (
				<Input
					name='title'
					max_length={15}
					_class={{ size:60 }}
					placeholder="Level"
				>Title</Input>
			)}

			{(can_delete) && (
				<Range
					min={5}
					max={95}
					step={5}
					_class={{ size:60 }}
					name="min_level"
					value={parseInt(model?.min_level ?? 10)}
					label_props={{
						_extra_component:i18n("in '%'"),
					}}
				>Starting row</Range>
			)}

			{/*<Range
				min={0}
				max={20}
				step={1}
				_class={{ size:50 }}
				name="multiplier"
				value={parseInt(model?.multiplier ?? 1)}
			>Score multiplier</Range>*/}

			<AutoPostImageUploader
				auto_url={`/promotions/auto_img/${props.promotion_id}`}
				auto_type='stages'
				changeMulti={props.changeMulti}
				hooks={auto_img_hooks}
				image={model.full_image}
				name="image"
				small_input={true}
				_size={{ width:150, height:150 }}
				_class={{ size:50 }}
			>Image</AutoPostImageUploader>

			<ColorPicker
				_class={{ size:60 }}
				_hide_code={true}
				name="color"
				color={model?.color ?? 'rgba(30, 34, 170, 1)'}
			>Color</ColorPicker>
		</Model>
	)
};

const MilestoneField = ({ model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const sounds_ok = [
		{ k:'none', v:i18n("None") },
		{ k:'applause', v:i18n("Applause") },
		{ k:'success', v:i18n("Success") },
	];

	const sounds_ko = [
		{ k:'none', v:i18n("None") },
		{ k:'evil_laugh', v:i18n("Evil laugh") },
		{ k:'funny_laugh', v:i18n("Funny laugh") },
		{ k:'fail', v:i18n("Fail trombone") },
		{ k:'oh', v:i18n("Oh") },
	];

	let type = 'milestone';
	if(model.id == 'success') type = 'success';
	if(model.id == 'out_of_time') type = 'time_up';

	let sound_fxs;
	let default_feedback_message;
	switch(type) {
		case 'success':
			sound_fxs = sounds_ok;
			default_feedback_message = `<h1>${i18n("Good job!")}</h1>`;
			break;

		case 'time_up':
			sound_fxs = sounds_ko;
			default_feedback_message = `<h1>${i18n("Almost!")}</h1>`;
			break;

		case 'milestone':
		default:
			sound_fxs = [...sounds_ok, ...sounds_ko.slice(1)];
			default_feedback_message = `<h1>${i18n("Good job!")}</h1>`;
			break;
	}

	return (
		<>
			<Model
				model={model}
				change={change}
				errors={props.errors != undefined && props?.errors[model.id]}
			>
				<AutoPostImageUploader
					auto_url={`/promotions/auto_img/${props.promotion_id}`}
					auto_type='stages'
					changeMulti={props.changeMulti}
					image={model.full_final_image}
					name="final_image"
					small_input={true}
					_size={{ fit:'auto' }}
				>Image</AutoPostImageUploader>

				<RichEditor
					name="feedback"
					placeholder={default_feedback_message}
					has_emojis={true}
				>Feedback message text</RichEditor>

				<SoundSelect
					name="sound_fx"
					values={sound_fxs}
					_class={{ size:60 }}
					value={model.sound_fx ?? sound_fxs[0].k}
				>Sound FX</SoundSelect>

				<ColorPicker
					_class={{ size:60 }}
					_hide_code={true}
					name="final_color"
					color={model?.final_color ?? (model?.color ?? 'green')}
				>Color</ColorPicker>
			</Model>
		</>
	)
};

const dynamic_fields_builder = (type, items) => {
	const last = items[items.length - 1];
	if(!last) return {};

	const values = {
		min_level:Math.min(parseInt(last.min_level) + 10, 90),
		multiplier:Math.min(parseInt(last.multiplier) + 1, 20),
		color:last.color,
	};

	return values;
};

const GameSettings = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const [ errors, setErrors ] = useState({});
	const [ loading, setLoading ] = useState(false);

	const min_time = 30;
	const max_time = 90;

	const min_rows = 15;
	const min_cols = 5;
	const default_cols = 6;
	const stack_num_rows = Math.max(parseInt(stage?.stack_num_rows ?? 30), min_rows);

	useEffect(() => {
		if(stage.stack_milestones == undefined) validate([]);
	}, []);

	const validate = (v) => {
		return true;

		v = (v == undefined) ? stage.smash_images : v;
		let errors = {};
		let total_images = v.length; //stage.smash_images != undefined ? Object.keys(stage.smash_images).length : 0;
		let errors_images = false;

		if(total_images < num_elements) {
			const remaining_images = num_elements - total_images;

			if(remaining_images > 1) {
				errors['smash_images'] = props.intl.formatMessage({ id:"Still missing {remain} items" }, { remain:remaining_images });
			} else {
				errors['smash_images'] = i18n("Still missing 1 item");
			}
		}

		if(errors_images) errors['images'] = i18n("Some items are missing information");

		setErrors(errors);

		setLoading(true);

		setTimeout(() => {
			setLoading(false);
		}, 0);
	};

	const getMinimumTimeToAchieve = (num_rows = stack_num_rows) => {
		// if the game_time is less than a specific value (dependant on the number of rows of the game),
		// the top level would not be reachable before the time is over,
		// so we enforce the user has enough time to reach the top level:
		return Math.min(max_time, Math.max(min_time, num_rows + 10))
	};

	const changeSidebar = (k, v) => {
		if(k == 'stack_num_rows') {
			const new_min_time_to_achieve = getMinimumTimeToAchieve(v);
			if(stage.game_time < new_min_time_to_achieve) change('game_time', new_min_time_to_achieve);
		}

		change(k, v);
		//validate();
	};

	const option_level = [
		{ k:'easy', v:i18n("Easy") },
		{ k:'normal', v:i18n("Normal") },
		{ k:'hard', v:i18n("***Hard") },
	];

	const max_cols = stack_num_rows - 4;
	const stack_initial_row = Math.min(Math.max(parseInt(stage?.stack_initial_row ?? default_cols), min_cols), max_cols);

	const min_time_to_achieve = getMinimumTimeToAchieve();

	return (
		<>
			<BuilderSidebar onSave={props.saveStage} errors={errors}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={changeSidebar}>
						<Select
							name="stack_level"
							values={option_level}
							_class={{size:40, first:true }}
							value={stage?.stack_level ?? 'normal'}
						>Level</Select>

						<Range
							min={min_rows}
							max={35}
							step={5}
							_class={{ size:50 }}
							name="stack_num_rows"
							value={stack_num_rows}
						>Number of rows</Range>

						<Range
							min={min_cols}
							max={max_cols}
							step={1}
							_class={{ size:50 }}
							name="stack_initial_row"
							value={stack_initial_row}
						>Base size</Range>

						<Range
							min={min_time_to_achieve}
							max={max_time}
							step={5}
							label_props={{
								addition:"In seconds",
							}}
							_class={{ size:50 }}
							name="game_time"
							value={(stage.game_time != undefined) ? Math.max(stage.game_time, min_time_to_achieve) : max_time}
						>Time of game</Range>
					</Model>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						{(loading) ? (
							<div class="gui_not_in_top gui_flex_center" style={{width:'100%',height:'100%'}}>
								<Spinner>Generating a new board</Spinner>
							</div>
						) : (
							(Object.keys(errors).length == 0) ? (
								<StackItClient
									key={`${stack_initial_row}_${stack_num_rows}`}
									stage={{ Stage:stage }}
									promotion={props.promotion}
									is_builder={true}
									is_stack={true}
									is_playable={true}
									section="settings"
								/>
							) : (
								<GameContainerWrapper>
									<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
										{Object.values(errors).map((error, i) => {
											let is_object = (typeof error === 'object') ? true : false;
											if(is_object) return null;
											return (
												<p key={`error${i}`}>{error}</p>
											)
										})}
									</div>
								</GameContainerWrapper>
							)
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameDesign = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ errors, setErrors ] = useState({});
	const [ loading, setLoading ] = useState(false);

	useEffect(() => {
		if(stage.stack_milestones == undefined) validate([]);
	}, []);

	const validate = (v) => {
		return true;

		v = (v == undefined) ? stage.smash_images : v;
		let errors = {};
		let total_images = v.length; //stage.smash_images != undefined ? Object.keys(stage.smash_images).length : 0;
		let errors_images = false;

		if(total_images < num_elements) {
			const remaining_images = num_elements - total_images;

			if(remaining_images > 1) {
				errors['smash_images'] = props.intl.formatMessage({ id:"Still missing {remain} items" }, { remain:remaining_images });
			} else {
				errors['smash_images'] = i18n("Still missing 1 item");
			}
		}

		if(errors_images) errors['images'] = i18n("Some items are missing information");

		setErrors(errors);

		setLoading(true);

		setTimeout(() => {
			setLoading(false);
		}, 0);
	};

	const changeSidebar = (k, v) => {
		change(k, v);
		//validate();
	};

	const min_rows = 15;
	const min_cols = 5;
	const default_cols = 6;
	const stack_num_rows = Math.max(parseInt(stage?.stack_num_rows ?? 30), min_rows);

	const max_cols = stack_num_rows - 4;
	const stack_initial_row = Math.min(Math.max(parseInt(stage?.stack_initial_row ?? default_cols), min_cols), max_cols);

	const section_title = `${i18n("Levels")} (${(stage?.stack_milestones ?? []).length})`;

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		})
	}

	const dynamic_fields = [{
		type:'milestone',
		label:"Level",
		translate:[
			{key:'title',type:'input'},
			{key:'feedback',type:'textarea'}
		],
		title:(f) => {
			const min_level = f?.min_level ?? 10;

			const milestone_level = Math.ceil((stack_num_rows + 1) * min_level / 100) + 1;

			if(isNotEmpty(f, 'title')) return props.intl.formatMessage({ id:"{title} (row {row})"}, { title:f.title, row:milestone_level});

			return props.intl.formatMessage({ id:"Row {row}" }, { row:milestone_level });
		},
		max:5,
	}];

	const layout_props = getLayout(props.promotion);

	const milestones = stage?.stack_milestones ?? [];
	if(milestones.length == 0) {
		milestones.push({
			id:generateUUID(),
			type:'milestone',
			title:i18n("Level 0"),
			min_level:0,
			color:layout_props.color.main_color,
			multiplier:1,
			can_delete:false,
			can_copy:false,
		});
	}

	const button_visibilities = [
		{ k:'always', v:i18n("Always") },
		{ k:'mobile', v:i18n("Only in mobile devices") },
		{ k:'hide', v:i18n("Never") },
	];

	const sounds_stack = [
		{ k:'none', v:i18n("None") },
		{ k:'pop', v:i18n("Pop") },
		{ k:'game_goal', v:i18n("Game goal") },
		{ k:'glockenspiel', v:i18n("Glockenspiel") },
		{ k:'unplug', v:i18n("Unplug") },
	];

	return (
		<>
			<BuilderSidebar onSave={props.saveStage} errors={errors}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<DynamicFields
						section_title={section_title}
						validator={dynamic_fields_builder}
						onChange={(v) => changeSidebar('stack_milestones', v)}
						field_types={dynamic_fields}
						has_translation={true}
						locales={avail_locales}
						add_button_props={{
							icon:'knk-add-item mainButton',
						}}
						sortable={false}
						max_items={5}
						fields={milestones}
					>
						<StackItField
							promotion_id={stage.promotion_id}
							errors={errors}
							intl={props.intl}
						/>
					</DynamicFields>

					<Model model={stage} change={changeSidebar}>
						<Checkbox
							name='stack_display_milestones'
						>Display levels</Checkbox>

						<Checkbox
							name='stack_display_grid'
						>Display grid</Checkbox>

						<Range
							min={0}
							max={100}
							step={10}
							_class={{ size:40 }}
							name="stack_block_corner_radius"
							value={stage?.stack_block_corner_radius ?? 0}
							label_props={{
								_extra_component:i18n("in '%'"),
							}}
						>Curvature of the blocks</Range>

						<Checkbox
							name='silence'
						>Disable sounds</Checkbox>

						{((stage?.silence ?? false) === false) && (
							<Select
								name="stack_row_soundfx"
								values={sounds_stack}
								_class={{ size:60 }}
								value={stage.stack_row_soundfx ?? 'pop'}
							>Sound FX</Select>
						)}
					</Model>

					<GameSoundtrackFields
						stage={stage}
						change={change}
					/>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Background"
						icon='knk-picture'
					/>

					<Model model={stage} change={changeSidebar}>
						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${stage.promotion_id}`}
							auto_type='stages'
							image={stage.full_bg_image}
							name="bg_image"
							small_input={true}
							_size={{ width:800, height:800 }}
						>Background image</AutoPostImageUploader>

						{(!isNotEmpty(stage, 'full_bg_image')) && (
							<ColorPicker
								_class={{ size:60 }}
								_hide_code={true}
								name="bg_color"
								color={stage?.bg_color ?? '#CCCCCC'}
							>Background color</ColorPicker>
						)}

						{(milestones.length > 1) && (
							<ColorPicker
								_class={{ size:60 }}
								_hide_code={true}
								name="stack_milestones_color"
								color={stage?.stack_milestones_color ?? 'rgba(0, 0, 0, 0.4)'}
							>Color of the level line</ColorPicker>
						)}

						<Checkbox
							name="display_outter_border"
							value={stage?.display_outter_border ?? true}
						>Display outer border</Checkbox>
					</Model>
				</article>

				<article class='gui_not_in_top_huge'>
					<BuilderSubSectionTitle
						title="STOP button options"
						icon='knk-button'
					/>

					<Model model={stage} change={changeSidebar}>
						<Select
							name="stack_button_display"
							values={button_visibilities}
							_class={{ size:60 }}
						>Display button</Select>
					</Model>

					{((stage?.stack_button_display ?? 'always') == 'mobile') && (
						<Message>
							<p>{i18n("In the editor, we always display the STOP button for you to customize. We confirm that it will only be visible on mobile devices.")}</p>
						</Message>
					)}

					{((stage?.stack_button_display ?? 'always') != 'hide') && (
						<>
							<Model model={stage} change={changeSidebar}>
								<AutoPostImageUploader
									auto_url={`/promotions/auto_img/${stage.promotion_id}`}
									auto_type='stages'
									image={stage.full_stack_button_image}
									name="stack_button_image"
									small_input={true}
									_size={{ width:300, height:300 }}
								>Background image</AutoPostImageUploader>

								{(isNotEmpty(stage, 'stack_button_image')) && (
									<AutoPostImageUploader
										auto_url={`/promotions/auto_img/${stage.promotion_id}`}
										auto_type='stages'
										image={stage.full_stack_button_hover_image}
										name="stack_button_hover_image"
										small_input={true}
										_size={{ width:300, height:300 }}
										autotranslate={false}
									>{`${i18n("Background image")} (${i18n("Hover", 'lowercase')})`}</AutoPostImageUploader>
								)}
							</Model>

							{(!isNotEmpty(stage, 'stack_button_image')) && (
								<Model model={stage} change={changeSidebar}>
									<InputPlaceHolder
										name="stack_button_label"
										_class={{ size:60 }}
										placeholder={i18n("Stop")}
										max_length={20}
									>Button label</InputPlaceHolder>

									<ColorPicker
										_class={{ size:60 }}
										_hide_code={true}
										name="stack_button_color"
										color={stage?.stack_button_color ?? layout_props.color.main_color}
									>Background color</ColorPicker>

									<Range
										min={0}
										max={15}
										step={1}
										_class={{ size:50 }}
										name="stack_border_width"
										value={stage?.stack_border_width ?? 10}
										label_props={{
											_extra_component:i18n("in 'px'"),
										}}
									>Border width</Range>

									{((stage?.stack_border_width ?? 0.6) > 0) && (
										<ColorPicker
											_class={{ size:60 }}
											_hide_code={true}
											name="stack_border_color"
											color={stage?.stack_border_color ?? 'rgba(0, 0, 0, 0.3)'}
										>Border color</ColorPicker>
									)}
								</Model>
							)}
						</>
					)}
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						{(loading) ? (
							<div class="gui_not_in_top gui_flex_center" style={{width:'100%',height:'100%'}}>
								<Spinner>Generating a new board</Spinner>
							</div>
						) : (
							(Object.keys(errors).length == 0) ? (
								<StackItClient
									key={`${stack_initial_row}_${stack_num_rows}`}
									stage={{ Stage:stage }}
									promotion={props.promotion}
									is_builder={true}
									is_stack={true}
									is_playable={false}
									section="design"
								/>
							) : (
								<GameContainerWrapper>
									<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
										{Object.values(errors).map((error, i) => {
											let is_object = (typeof error === 'object') ? true : false;
											if(is_object) return null;
											return (
												<p key={`error${i}`}>{error}</p>
											)
										})}
									</div>
								</GameContainerWrapper>
							)
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const generateSuccess = (stage, i18n) => {
	const states = [{
		id:'success',
		type:'milestone',
		sound_fx:stage?.game_soundfx_ok ?? 'applause',
		feedback:stage?.game_feedback_ok ?? `<h1>${i18n("Good job!")}</h1>`,
		final_color:stage?.game_color_ok ?? 'rgba(120, 195, 0, 1)',
		final_image:stage?.game_final_image_ok ?? null,
		full_final_image:stage?.full_game_final_image_ok ?? null,
		title:stage?.game_feedback_ok ?? "Top",
	}, {
		id:'out_of_time',
		type:'milestone',
		sound_fx:stage?.game_soundfx_ko_time ?? 'evil_laugh',
		feedback:stage?.game_feedback_ko_time ?? `<h1>${i18n("Almost!")}</h1>`,
		final_color:stage?.game_color_ko_time ?? 'rgba(215, 35, 60, 1)',
		final_image:stage?.game_final_image_ko_time ?? null,
		full_final_image:stage?.full_game_final_image_ko_time ?? null,
		title:stage?.game_feedback_ko_time ?? "Out of time",
		style:{
			order:'1',
			borderTop:'1px solid rgba(var(--main-text-color), 0.4)',
			borderBottom:'none',
		},
	}];

	if(stage.stack_milestones != undefined && stage.stack_milestones.length > 0) {
		(stage.stack_milestones.sort((a, b) => {
			return (parseInt(a.min_level) > parseInt(b.min_level)) ? -1 : 1;
		})).map((i) => {
			states.push(i);
		});
	}

	return states;
};

const GameScore = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ final_case, setFinalCase ] = useState(false);

	const generated_fields = generateSuccess(stage, i18n);

	const dynamic_fields = [{
		type:'milestone',
		label:"Level",
		title:(f) => {
			const type = f?.id ?? '';
			switch(type) {
				case 'success':
					return i18n("Tower completed");

				case 'out_of_time':
					return i18n("Time out");

				default:
					const min_level = f?.min_level ?? 10;

					if(generated_fields.length < 4) {
						// no extra milestone created:
						// [minimum of generated_fields: 3 (milestone @ 0% + ko_time + ok)]
						return i18n("Incomplete tower");
					}

					if(min_level < 1) {
						return i18n("Level 0");
					}

					return f?.title ?? props.intl.formatMessage({ id:"Level {level} ({percent}%)" }, { level:milestone_level, percent:min_level });
			}
		},
	}];
 
	const changeState = (v) => {
		const state_ok = v[0];
		const state_time = v[1];
		const data = {
			game_feedback_ok:state_ok.feedback,
			game_color_ok:state_ok.final_color,
			game_soundfx_ok:state_ok.sound_fx,
			game_final_image_ok:state_ok.final_image,
			game_feedback_ko_time:state_time.feedback,
			game_color_ko_time:state_time.final_color,
			game_soundfx_ko_time:state_time.sound_fx,
			game_final_image_ko_time:state_time.final_image,
			stack_milestones:v.slice(2),
		};

		props.changeMulti(data);
	};

	const actual_state = (final_case !== false) ? generated_fields.find((i) => (i.id == final_case)) : null;
	stage.display_status = (actual_state?.is_success ?? true) ? 'success' : 'fail_attempts';

	return (
		<>
			<BuilderSidebar onSave={props.saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<DisplayList
					onChange={changeState}
					field_types={dynamic_fields}
					fields={generated_fields}
					onOpen={(actual) => {
						setFinalCase(actual.id);
					}}
					section_icon={false}
					section_title={i18n("What do you want to customize?")}
				>
					<MilestoneField
						promotion_id={stage.promotion_id}
						changeMulti={props.changeMulti}
						intl={props.intl}
					/>
				</DisplayList>

				<Model model={stage} change={change}>
					<Checkbox
						name="hide_score_resumee"
					>Hide score</Checkbox>

					<Checkbox
						name="override_promo_styles"
						value={stage?.override_promo_styles ?? false}
					>Override promotion styles</Checkbox>
				</Model>
			</BuilderSidebar>

			<BuilderCanvas is_adaptable={true} fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div
						class="gui_withoutEvents"
						style={{ width:'100%', height:'100%' }}
					>
						<StackItClient
							stage={{ Stage:stage }}
							promotion={props.promotion}
							is_builder={true}
							section="score"
							actual_state={actual_state}
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameTest = ({ stage, change, ...props }) => {
	const restart = () => {
		change('reset', (stage.reset != undefined) ? (stage.reset + 1) : 0);
	};

	return (
		<>
			<BuilderSidebar without_ctas={true}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<ButtonCollection>
					<ActionButton
						icon='knk-reload'
						onClick={() => restart()}
					>Restart game</ActionButton>
				</ButtonCollection>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout promo={props.promotion} is_builder={true} page="quiz" menus={props.promoModel.Custom__Menus} layout_extra_classes="is_new_game">
					<div style={{width:'100%'}}>
						<StackItClient
							stage={{ Stage:stage }}
							promotion={props.promotion}
							is_builder={true}
							is_test={true}
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const StackItBuilder = ({ base_route,stage, ...props }) => {
	const basename = `/stages/${stage.id}/tower`;
	const nav_sections = [
		{ label:"Go back", link:`/stages/${stage.id}/options`, icon:'knk-go-prev' },
		{ label:"Gameplay settings", link:`${basename}/settings`, icon:'knk-settings' },
		{ label:"Design", link:`${basename}/design`, icon:'knk-artist-mode' },
		{ label:"Instructions", link:`${basename}/instructions`, icon:'knk-help' },
		{ label:"Score screen", link:`${basename}/gamescore`, icon:'knk-like' },
		{ label:"Test the game", link:`${basename}/test`, icon:'knk-ok' },
	];

	const common_props = {
		stage,
		is_multistage:props.is_multistage,
		isCorporate:props.isCorporate,
		change:props.change,
		changeMulti:props.changeMulti,
		intl:props.intl,
		promotion:props.promotion,
		promoModel:props.promoModel,
		saveStage:props.saveStage,
		stage_indicator:props.stage_indicator,
		nav_sections:nav_sections,
	};


	return (
		<BuilderMain pm={props.promoModel}>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/settings">
						<GameSettings
							{...common_props}
							nav_active={`/settings`}
						/>
					</Route>

					<Route exact={true} path="/design">
						<GameDesign
							{...common_props}
							nav_active={`/design`}
						/>
					</Route>

					<Route exact={true} path="/instructions">
						<GameInstructionsBuilder
							{...common_props}
							nav_active={`/instructions`}
						>
							<StackItClient
								stage={{ Stage:stage }}
								promotion={props.promotion}
								is_builder={true}
								section="instructions"
							/>
						</GameInstructionsBuilder>
					</Route>

					<Route exact={true} path="/gamescore">
						<GameScoreBuilder
							{...common_props}
							nav_active={`/gamescore`}
							exclude_cases={['out_of_moves']}
						>
							<StackItClient
								stage={{ Stage:stage }}
								promotion={props.promotion}
								is_builder={true}
								section="score"
							/>
						</GameScoreBuilder>
						{/* <GameScore
							change={props.change}
							changeMulti={props.changeMulti}
							intl={props.intl}
							nav_active={`/gamescore`}
							nav_sections={nav_sections}
							promotion={props.promotion}
							promoModel={props.promoModel}
							saveStage={props.saveStage}
							stage={props.stage}
							stage_indicator={props.stage_indicator}
						/> */}
					</Route>

					<Route exact={true} path="/test">
						<GameTest
							{...common_props}
							nav_active={`/test`}
						/>
					</Route>
				</Switch>
			</Router>
		</BuilderMain>
	)
};

export default injectIntl(StackItBuilder);